import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import Swal from 'sweetalert2';
import { DataUpdateRequestService } from 'src/app/dashboard/services/data-update-request.service';

/**
 * Whitelist de endpoints/métodos permitidos cuando `bloqueado === true`.
 * Sincronizada con §6.5 del plan: incluye endpoints del flujo de
 * actualización, consultas disfrazadas de POST (AC + platform-messages),
 * MFA OTP y `userContactData`.
 *
 * Cualquier escritura que no matchee ninguno de estos patrones se
 * rechaza con 423 sintético + redirige al formulario.
 */
const WHITELIST_PATTERNS: ReadonlyArray<RegExp> = [
  // Flujo de actualización (obligatorios)
  /\/data-update-requests\/[^/]+\/submit$/,
  /\/data-update-requests\/[^/]+\/items\/[^/]+\/file-upload-url$/,
  /\/data-update-requests\/[^/]+\/items\/[^/]+\/file-confirm$/,

  // Consultas disfrazadas de POST (mantienen UX read-only)
  /\/transactionAc\/(pending|completed)$/,
  /\/platform-messages\/(unread-count|sent|inbox|pending|read-all)$/,
  /\/platform-messages\/[^/]+\/read$/,

  // MFA / auth
  /\/otp\/(send|verify|resend)$/,

  // Decisión explícita: permite update directo de contacto
  /\/userContactData$/,
];

/**
 * Interceptor del bloqueo determinante. Corre DESPUÉS de
 * `ApiRestInterceptor` (token ya inyectado) y ANTES de `ErrorInterceptor`
 * — orden definido en `interceptor.provider.ts`.
 *
 * Reglas:
 *  - GETs y HEADs siempre pasan (consultas read-only no se bloquean).
 *  - PUT a host AWS S3 (URL pre-firmada) pasa — es upload del flujo.
 *  - Si status local no está `bloqueado` → pasa.
 *  - Si está `bloqueado` y matchea whitelist → pasa.
 *  - Resto → throwError(423) + navigate al formulario.
 *
 * Honesta admisión: el bloqueo es front-only (ver §9 del plan). Un
 * cliente técnico con DevTools puede burlar esto. La barrera real es la
 * UX + el respeto al contrato; cuando exista enforcement server-side
 * se desarma esta lógica.
 */
@Injectable()
export class DataUpdateBlockInterceptor implements HttpInterceptor {
  /** Destino al que volvemos al cliente cuando bloqueamos una escritura. */
  private static readonly HOME_URL = '/wallet/home';
  private static readonly S3_HOST_PATTERN = /\.amazonaws\.com\//;

  /**
   * Throttle del toast: si el cliente intenta operar varias veces rápido
   * (o algún componente dispara N requests), no queremos N modales
   * encima. Mantenemos `lastNotifiedAt` y silenciamos avisos repetidos
   * dentro de la misma ventana.
   */
  private lastNotifiedAt: number = 0;
  private static readonly NOTIFY_THROTTLE_MS = 4_000;

  constructor(
    private service: DataUpdateRequestService,
    private router: Router,
  ) {}

  intercept(
    req: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    const method = req.method.toUpperCase();

    if (method === 'GET' || method === 'HEAD') {
      return next.handle(req);
    }

    const status = this.service.status;
    if (!status.bloqueado) {
      // No bloqueado a nivel front, pero el cache puede estar stale
      // (operador creó una solicitud y el cliente todavía no se enteró).
      // Dejamos pasar la request, pero si el backend rebota con un
      // código compatible con "solicitud pendiente" refrescamos el
      // status y redirigimos al home en background.
      return next.handle(req).pipe(
        catchError((error: HttpErrorResponse) => {
          if (
            this.isLikelyPendingUpdateError(error) &&
            !this.isWhitelistedUrl(req)
          ) {
            this.refreshAndRedirectIfBlocked();
          }
          return throwError(() => error);
        }),
      );
    }

    if (
      method === 'PUT' &&
      DataUpdateBlockInterceptor.S3_HOST_PATTERN.test(req.url)
    ) {
      return next.handle(req);
    }

    if (this.isWhitelistedUrl(req)) {
      return next.handle(req);
    }

    // Llevamos al cliente al home — UserHomeComponent embebe el form
    // de revisión con microcopy amigable. Evitamos navegar si ya está
    // en el home (no genera nuevo entry en history).
    if (!this.router.url.startsWith(DataUpdateBlockInterceptor.HOME_URL)) {
      this.router.navigate([DataUpdateBlockInterceptor.HOME_URL]);
    }

    this.notifyClientThrottled();

    return throwError(() => ({
      status: 423,
      error: { code: 'USER_BLOCKED_BY_PENDING_UPDATE' },
    }));
  }

  /** Helper para chequear la whitelist desde múltiples paths del intercept. */
  private isWhitelistedUrl(req: HttpRequest<unknown>): boolean {
    return WHITELIST_PATTERNS.some((re) => re.test(req.url));
  }

  /**
   * Detecta si un error HTTP "huele a" solicitud de actualización
   * pendiente. Sin contrato cerrado con backend, somos permisivos:
   *  - HTTP 423 (Locked) — el código semánticamente correcto.
   *  - código de error con "PENDING_UPDATE" o "DATA_UPDATE" en el nombre.
   *  - 4xx con `message` que mencione "actualización" o "datos pendientes".
   *
   * Es defensa en profundidad: aunque normalmente el front se entera
   * por polling, si el backend rebota antes lo aprovechamos.
   */
  private isLikelyPendingUpdateError(error: HttpErrorResponse): boolean {
    if (!error) return false;
    if (error.status === 423) return true;
    const code = error?.error?.code;
    if (typeof code === 'string') {
      const upper = code.toUpperCase();
      if (upper.includes('PENDING_UPDATE') || upper.includes('DATA_UPDATE')) {
        return true;
      }
    }
    const msg = error?.error?.message;
    if (typeof msg === 'string') {
      const lower = msg.toLowerCase();
      if (
        (lower.includes('actualiz') || lower.includes('datos pendient')) &&
        error.status >= 400 &&
        error.status < 500
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * Llamado tras detectar un error que sugiere bloqueo. Refresca el
   * status; si efectivamente quedó bloqueado, redirige al home (donde
   * UserHomeComponent muestra el form embebido).
   */
  private refreshAndRedirectIfBlocked(): void {
    this.service.refresh().then((status) => {
      if (
        status?.bloqueado &&
        !this.router.url.startsWith(DataUpdateBlockInterceptor.HOME_URL)
      ) {
        this.router.navigate([DataUpdateBlockInterceptor.HOME_URL]);
        this.notifyClientThrottled();
      }
    });
  }

  /**
   * Toast suave para que el cliente sepa por qué no pasó la operación.
   * Se evita repetirlo dentro de un margen corto (NOTIFY_THROTTLE_MS).
   */
  private notifyClientThrottled(): void {
    const now = Date.now();
    if (
      now - this.lastNotifiedAt <
      DataUpdateBlockInterceptor.NOTIFY_THROTTLE_MS
    ) {
      return;
    }
    this.lastNotifiedAt = now;
    Swal.fire({
      toast: true,
      position: 'top-end',
      icon: 'info',
      title: 'Tu información está en revisión',
      text: 'Te avisaremos cuando puedas volver a operar.',
      showConfirmButton: false,
      timer: 3500,
      timerProgressBar: true,
    });
  }
}
